import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { getDistanceBetweenPoints, scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { primaryDrawingColor } from '../../../../assets/colors'

export type CircleDrawingState = {
    center?: Point,
    handle?: Point
}

export type CircleDrawingSettings = {
    fillColor: ColorValue,
    lineColor: ColorValue,
    lineWidth: number,
    labelColor: ColorValue
}

class CircleDrawingTool extends DrawingBase<any, CircleDrawingSettings, CircleDrawingState> {

    scaledCoordinates = {
        centerX: 0,
        centerY: 0,
        handleX: 0,
        handleY: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateAttributes()
        let { centerX, centerY, handleX, handleY } = this.scaledCoordinates
        if (!centerX || !centerY || !handleX || !handleY) return

        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.strokeStyle = this._settings.lineColor.color
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio

        ctx.beginPath()
        ctx.arc(centerX, centerY, Math.abs(handleX - centerX), 0, 2 * Math.PI)

        ctx.stroke()
        ctx.closePath()
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillStyle = this._settings.fillColor.color
        ctx.fill()
    };

    calculateAttributes() {
        const { center, handle } = this._state
        if (!center || !handle) return

        let cX = this.timeToCoordinate(center.x)
        let hX = this.timeToCoordinate(handle.x)
        let cY = this.priceToCoordinate(center.y)
        let hY = this.priceToCoordinate(handle.y)
        if (cX && cY && hX && hY) {
            this.scaledCoordinates.centerX = scaledCoordinate(cX, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.centerY = scaledCoordinate(cY, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.handleX = scaledCoordinate(hX, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.handleY = scaledCoordinate(hY, this.verticalPixelRatio) as Coordinate
        }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const { center } = this._state
        const point: Point = {
            id: 'center',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.center = point
        } else if (this.lastAddedPoint.id == 'center') {
            point.id = 'handle'
            point.y = center!.y
            this._state.handle = point

            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { center, handle } = this._state

        center && handles.push(center)
        handle && handles.push(handle)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { centerX, centerY, handleX } = this.scaledCoordinates
        if (getDistanceBetweenPoints(centerX, scaledX, centerY, scaledY) <= Math.abs(handleX - centerX)) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }

        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'center':
                    this._state.center = h
                    break
                case 'handle':
                    this._state.handle = h
                    break
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        switch (id) {
            case 'center':
                this._state.center = {
                    id,
                    x: time,
                    y: price
                }

                this._state.handle!.y = price
                break
            case 'handle':
                this._state.handle!.x = time
        }

    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { center, handle } = this._state
        if (!center || !handle) return []

        return [{
            id: 'price',
            p1: center.y,
            p2: center.y,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { center } = this._state
        if (!center) return []

        return [{
            id: 'time',
            p1: center.x,
            p2: center.x,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<CircleDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#ff9900',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: { color: '#ff9900', opacity: 1 },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): CircleDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof CircleDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'circle'
    }
}

export default CircleDrawingTool